import { Api } from "@/services/mrfiktiv/api";
import { ConfigModule } from "@/store/modules/config";
import axios from "axios";
import Vue from "vue";
import {
  AuthAccessToken,
  ChangePasswordIfRequiredDto,
  ConfirmPasswordDto,
  ForgotPasswordDto,
  GetTokenByRefreshTokenDto,
  UserAuthDto
} from "../v1/mrfiktiv.api";

class AuthService {
  ROUTE = "/auth";

  /**
   * Used get access token and refreshtoken
   * @param signInDto
   * @returns AuthAccessToken
   */
  async signIn(signInDto: UserAuthDto): Promise<AuthAccessToken> {
    this.validateInput(signInDto.username, "username");
    this.validateInput(signInDto.password, "password");

    const api = new Api().instance();
    let signIn: AuthAccessToken;
    try {
      const response = await api.post(`${this.ROUTE}/signin`, signInDto);
      signIn = response.data as AuthAccessToken;
    } catch (error) {
      Vue.$log.error(error);
      throw error;
    }

    return signIn;
  }

  private validateInput(input: string, type: string) {
    if (!input) {
      const error = `${type} not set`;
      Vue.$log.error(error);
      throw Error(error);
    }
  }

  /**
   * invalidates refresh token
   */
  async signOut() {
    const api = new Api().instance();

    try {
      await api.get(`${this.ROUTE}/signOut`);
    } catch (error) {
      Vue.$log.error(error);
      throw error;
    }
  }

  /**
   * Refresh access token
   * @param getTokenByRefreshTokenDto
   * @returns AuthAccessToken
   */
  async refresh(getTokenByRefreshTokenDto: GetTokenByRefreshTokenDto): Promise<AuthAccessToken> {
    const client = axios.create({
      baseURL: ConfigModule.mrfiktivBaseUrl,
      timeout: 20000
    });

    let refresh: AuthAccessToken;
    try {
      const response = await client.post(`${this.ROUTE}/refresh`, getTokenByRefreshTokenDto);
      refresh = response.data as AuthAccessToken;
    } catch (error) {
      Vue.$log.error(error);
      throw error;
    }
    return refresh;
  }

  /**
   * Route when user forgot password.
   * @param confirmPasswordDto
   */
  async confirmPassword(confirmPasswordDto: ConfirmPasswordDto) {
    const api = new Api().instance();
    try {
      await api.post(`${this.ROUTE}/confirmPassword`, confirmPasswordDto);
    } catch (error) {
      Vue.$log.error(error);
      throw error;
    }
  }

  /**
   * Route when user signs in for first time and has to reset the remporary password.
   * @param changePasswordIfRequiredDto
   * @return AuthAccessToken
   */
  async changePasswordIfRequired(changePasswordIfRequiredDto: ChangePasswordIfRequiredDto): Promise<AuthAccessToken> {
    const api = new Api().instance();
    let changePasswordIfRequired: AuthAccessToken;
    try {
      const response = await api.post(`${this.ROUTE}/changePasswordIfRequired`, changePasswordIfRequiredDto);
      changePasswordIfRequired = response.data as AuthAccessToken;
    } catch (error) {
      Vue.$log.error(error);
      throw error;
    }
    return changePasswordIfRequired;
  }

  /**
   * Route when user signs in for first time and has to reset the remporary password.
   * @param changePasswordIfRequiredDto
   */
  async forgotPassword(forgotPasswordDto: ForgotPasswordDto) {
    const api = new Api().instance();
    try {
      await api.post(`${this.ROUTE}/forgotPassword`, forgotPasswordDto);
    } catch (error) {
      Vue.$log.error(error);
      throw error;
    }
  }

  async authRequestUser() {
    const api = new Api().instance();
    const authRequestUser = await api.get("/authRequestUser");
    return authRequestUser.data;
  }
}

export default new AuthService();
